import { action, computed, reaction, observable, makeObservable } from 'mobx';
import { clientId, Disposers } from '../../../../../../lib/helpers';
import { X9ContentStateStore } from '../../../common/X9ContentStateStore';
import { SyncedVideoModel } from '../../models';
import { VideoElementStore } from '../common/VideoElementStore';
import { VideoProgressStore } from '../common/VideoProgressStore';
import { PlayTimeRange, StudentPlayerState, SyncedVideoState } from '../models';

export class StudentSyncedVideoStore {
  readonly video = new VideoElementStore();
  readonly progress = new VideoProgressStore();

  private readonly disposers = new Disposers();

  @observable.shallow
  readonly trackedStudents = new Map<number, VideoProgressStore>();

  private playCount = Number.MIN_SAFE_INTEGER;

  constructor(readonly id: number,
    readonly model: SyncedVideoModel,
    readonly stateStore: X9ContentStateStore) {
    makeObservable(this);
    this.disposers.add(this.video);
    this.disposers.add(this.progress);
    this.setup();
  }

  private setup() {
    this.progress.syncVideoDurationFrom(this.video);
    this.progress.syncBufferedFrom(this.video);
    this.progress.syncCurrentTimeFrom(this.video);

    // this.disposers.add(reaction(
    //   () => this.syncedVideoStudents,
    //   this.addCurrentClientId,
    //   {fireImmediately: true}));
    this.disposers.add(reaction(
      () => this.syncedVideoStudents,
      this.addCurrentClientId));
    this.disposers.add(reaction(
      () => [this.video.currentTime, this.video.buffered],
      this.notifyStudentState,
      { fireImmediately: true }));
    this.disposers.add(reaction(
      () => this.playCommand,
      this.executePlayCommand,
      { fireImmediately: true }
    ));
    this.disposers.add(reaction(
      () => this.video.currentTime,
      this.stopPlayRange,
      { fireImmediately: true }
    ));
  }

  @computed
  private get syncedVideoStudents(): SyncedVideoState {
    const studentIds: number[] = [0];
    const altSyncedVideoStudents: SyncedVideoState = { studentIds };
    return this.stateStore.getContentState<SyncedVideoState>(this.id) ? this.stateStore.getContentState<SyncedVideoState>(this.id)! : altSyncedVideoStudents;
  }

  @action
  private addCurrentClientId = () => {
    const students = this.syncedVideoStudents;
    const studentIds = students.studentIds.slice();
    studentIds.push(clientId);
    this.stateStore.setContentState<SyncedVideoState>(this.id, { studentIds });
  };

  @computed
  private get studentState(): StudentPlayerState {
    return {
      currentTime: this.video.currentTime,
      buffered: this.video.buffered
    };
  }

  @action
  private notifyStudentState = () => {
    this.stateStore.setScopedContentState<StudentPlayerState>(this.id, clientId, this.studentState);
  };

  @computed
  private get playCommand(): PlayTimeRange | null {
    return this.stateStore.getScopedContentState(this.id, 'command');
  }

  @action
  executePlayCommand = () => {
    const cmd = this.playCommand;
    if (cmd === null || cmd.start === null) {
      this.video.pause();
      return;
    }
    if (cmd.commandId === this.playCount)
      return;
    this.playCount = cmd.commandId;
    this.video.setCurrentTime(cmd.start);
    this.video.play();
  };

  @action
  stopPlayRange = () => {
    const cmd = this.playCommand;
    if (cmd === null || cmd.start === null) {
      // this.video.pause();
      return;
    }
    if (cmd.stop === null)
      return;
    if (cmd.stop < this.video.currentTime)
      this.video.pause();
  };

  dispose() {
    this.disposers.dispose();
  }
}
